import { useCallback, useState } from "react";

import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import { logError } from "@hl/shared-features/lib/services/logger";

import {
  PriceType,
  OnChainMintVectorCreateInput,
  OnChainMintVectorUpdateInput,
  OnChainDutchAuctionCreateInput,
  OnChainDutchAuctionUpdateInput,
  _CollectionType,
  OnChainRankedAuctionUpdateInput,
} from "apollo/graphql.generated";
import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import { useUpdateaRankedAuctionOnChain } from "~/features/tokens/sections/ManageToken/MintVector/ranked-auction-tx";
import useCreateDutchAuctionOnChain from "~/features/tokens/sections/ManageToken/MintVector/useCreateDutchAuctionOnChain";
import useCreateMintVectorOnChain from "~/features/tokens/sections/ManageToken/MintVector/useCreateMintVectorOnChain";
import {
  SaleForm,
  SaleType,
} from "~/features/tokens/sections/ManageToken/MintVector/useSaleForm";
import useUpdateDutchAuctionOnChain from "~/features/tokens/sections/ManageToken/MintVector/useUpdateDutchAuctionOnChain";
import useUpdateMintVectorOnChain from "~/features/tokens/sections/ManageToken/MintVector/useUpdateMintVectorOnChain";
import {
  GetManageTokenCollectionDocument,
  GetManageTokenMintVectorsDocument,
  useCreateManageTokenMintVectorMutation,
  useUpdateManageTokenMintVectorMutation,
} from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

const useManageSale = (
  collection: {
    collectionType: _CollectionType;
    id: string;
    reveal: boolean | null;
  },
  onClose: () => void,
  mintVector?: GetManageCollectionQuery["getManageCollection"]["mintVectors"][0]
) => {
  const [error, setError] = useState("");
  const { trackEvent, EventType } = useAnalytics();

  const [updateMintVector, { loading: loadingUpdate }] =
    useUpdateManageTokenMintVectorMutation({
      refetchQueries: [
        GetManageTokenCollectionDocument,
        GetManageTokenMintVectorsDocument,
      ],
    });

  const [createMintVector, { loading: loadingCreate }] =
    useCreateManageTokenMintVectorMutation({
      refetchQueries: [
        GetManageTokenCollectionDocument,
        GetManageTokenMintVectorsDocument,
      ],
    });

  const {
    startUpdate: startOnchainUpdate,
    buttonLabel: buttonLabelUpdate,
    buttonDisabled: buttonDisabledUpdate,
    error: errorUpdateVector,
  } = useUpdateMintVectorOnChain({
    mintVectorId: mintVector?.id || "",
    collectionType: collection.collectionType,
    collectionId: collection.id,
    onFinish: onClose,
  });

  const {
    startUpdate: startOnchainUpdateDutchAuction,
    buttonLabel: buttonLabelUpdateDutchAuction,
    buttonDisabled: buttonDisabledUpdateDutchAuction,
    updatePause: daUpdatePause,
    error: errorUpdateDutchAuction,
  } = useUpdateDutchAuctionOnChain({
    mintVectorId: mintVector?.id || "",
    collectionType: collection.collectionType,
    collectionId: collection.id,
    onFinish: onClose,
  });

  const {
    startUpdate: startOnchainUpdateRankedAuction,
    buttonLabel: buttonLabelUpdateRankedAuction,
    buttonDisabled: buttonDisabledUpdateRankedAuction,
    error: errorUpdateRankedAuction,
    updatePause: raUpdatePause,
  } = useUpdateaRankedAuctionOnChain({
    mintVectorId: mintVector?.id || "",
    collectionType: collection.collectionType,
    collectionId: collection.id,
    onFinish: onClose,
  });

  const handleSubmit = useCallback(
    async (values: SaleForm) => {
      setError("");
      try {
        const doesMintVectorExist = mintVector?.id != null;
        const isPublic = values.saleType === SaleType.PUBLIC;
        const isDutchAuction =
          values.mintDetails.priceType === PriceType.DutchAuction;
        const isRankedAuction =
          values.mintDetails.priceType === PriceType.RankedAuction;
        const isCollectorsChoice =
          collection.collectionType === _CollectionType.Series &&
          collection.reveal;

        const resolveValues = () => {
          const onChainSubmissionCore = {
            start: values.saleDateFrom.toString(),
            end: values.saleDateTo?.toString() || null,
            maxPerUser: values.shouldLimitNumberOfMintPerWallet
              ? values.limitPerWallet
              : 0,
            maxPerVector: values.saleSize,
            priceDropInterval: values.priceDropInterval?.map((period) => ({
              unit: period.unit,
              value: period.value,
            })),
            scheduledPrices: values.scheduledPrices ?? undefined,
          };

          return {
            ...onChainSubmissionCore,
            ...(!isPublic
              ? {
                  gateId: values.gateId ?? "",
                  name: values.saleName,
                  collectorMessage: values.collectorMessage,
                }
              : {}),
          };
        };

        const submission = resolveValues();
        const price = values.price == null ? "" : values.price.toString();
        if (doesMintVectorExist) {
          trackEvent(EventType.EditToken_EditSaleSaved, submission);

          if (isPublic) {
            if (isDutchAuction) {
              await startOnchainUpdateDutchAuction({
                ...submission,
                start: mintVector?.stats?.sold ? undefined : submission.start, // if some tokens are sold, avoid hitting guard in backend / contract
              } as OnChainDutchAuctionUpdateInput);
            } else if (isRankedAuction) {
              await startOnchainUpdateRankedAuction({
                ...submission,
                reserveBid: values.rankedAuction?.reserveBidAmount.toString(),
                // TODO:
                // rebateWinningBids: values.rankedAuction?.rebateWinningBids,
                currency: values.currency,
              } as OnChainRankedAuctionUpdateInput);
            } else {
              await startOnchainUpdate({
                ...submission,
                paused: values.isPaused,
                price,
                currency: values.currency,
              } as OnChainMintVectorUpdateInput);
            }
          } else {
            if (mintVector?.id == null) throw new Error("No vector id");

            await updateMintVector({
              variables: {
                data: {
                  paused: values.isPaused,
                  currency: values.currency,
                  mintVectorId: mintVector.id,
                  ...submission,
                  price,
                },
              },
            });
            onClose();
          }
        } else {
          if (isPublic) {
            if (isDutchAuction) {
              await startCreateDutchAuction({
                ...submission,
                maxPerTxn: 0,
                paymentRecipient: values.paymentRecipient,
              } as OnChainDutchAuctionCreateInput);
            } else {
              await startCreate({
                ...submission,
                maxPerTxn: 0,
                price,
                paymentRecipient: values.paymentRecipient,
                currency: values.currency,
              } as OnChainMintVectorCreateInput);
            }
          } else {
            await createMintVector({
              variables: {
                data: {
                  ...submission,
                  collectionId: collection.id,
                  priceType: values.mintDetails.priceType,
                  currency: values.currency,
                  maxPerTxn: isCollectorsChoice ? 1 : 0,
                  price,
                  paymentRecipient: values.paymentRecipient,
                },
              },
            });
            onClose();
          }
        }
      } catch (e) {
        setError("Failed to save changes");
        await logError({
          e,
          message: "[ManageSaleModal] Failed to save mint vector",
        });
      }
    },
    [mintVector?.id, collection]
  );

  const {
    startCreate,
    buttonLabel: buttonLabelCreate,
    buttonDisabled: buttonDisabledCreate,
  } = useCreateMintVectorOnChain({
    collectionType: collection.collectionType,
    collectionId: collection.id,
    onFinish: onClose,
  });

  const {
    startCreate: startCreateDutchAuction,
    buttonLabel: buttonLabelCreateDutchAuction,
    buttonDisabled: buttonDisabledCreateDutchAuction,
  } = useCreateDutchAuctionOnChain({
    collectionType: collection.collectionType,
    collectionId: collection.id,
    onFinish: onClose,
  });

  const updatePaymentRecipientForPublicSale = useCallback(
    async (paymentRecipient: string) => {
      setError("");
      try {
        if (!mintVector) throw new Error("Sale is required");
        if (!mintVector.isPublic) throw new Error("Sale should be public");
        const isDutchAuction = mintVector.priceType === PriceType.DutchAuction;
        if (isDutchAuction) {
          await startOnchainUpdateDutchAuction({
            paymentRecipient,
          } as OnChainDutchAuctionUpdateInput);
        } else {
          await startOnchainUpdate({
            paymentRecipient,
          } as OnChainMintVectorUpdateInput);
        }
      } catch (e) {
        setError("Failed to save changes");
        await logError({
          e,
          message: "[UpdatePayout] Failed to update collection",
        });
      }
    },
    [mintVector?.priceType]
  );

  const updatePauseForAuctionSale = useCallback(
    async (paused: boolean) => {
      setError("");
      try {
        if (!mintVector) throw new Error("Sale is required");
        const isDutchAuction = mintVector.priceType === PriceType.DutchAuction;
        const isRankedAuction =
          mintVector.priceType === PriceType.RankedAuction;

        if (mintVector.isPublic && isDutchAuction) {
          await daUpdatePause(paused);
        } else if (isRankedAuction) {
          await raUpdatePause(paused);
        } else {
          throw new Error("Sale should be public DA or Ranked Auction");
        }
      } catch (e) {
        setError("Failed to save changes");
        await logError({
          e,
          message: "[UpdatePause] Failed to update sale",
        });
      }
    },
    [mintVector]
  );

  return {
    buttonDisabled:
      buttonDisabledUpdate ||
      buttonDisabledCreate ||
      buttonDisabledUpdateDutchAuction ||
      buttonDisabledCreateDutchAuction ||
      buttonDisabledUpdateRankedAuction,
    buttonLabel:
      buttonLabelUpdate ||
      buttonLabelCreate ||
      buttonLabelUpdateDutchAuction ||
      buttonLabelCreateDutchAuction ||
      buttonLabelUpdateRankedAuction,
    loading: loadingCreate || loadingUpdate,
    error:
      error ||
      errorUpdateDutchAuction ||
      errorUpdateVector ||
      errorUpdateRankedAuction,
    handleSubmit,
    updatePaymentRecipientForPublicSale,
    updatePauseForAuctionSale,
  };
};

export default useManageSale;
